/**
 * Query key factory.
 *
 * Cache invalidation is the thing that quietly rots in a growing app: a
 * stringly-typed key written slightly differently in two files means a screen
 * that silently shows stale data. Every key comes from here, so invalidating
 * "all unit lists" is one call and every usage is greppable.
 */
export const queryKeys = {
    me: () => ['me'],
    storeSettings: () => ['store-settings'],
    integrationSettings: () => ['integration-settings'],
    taxCategories: () => ['tax-categories'],
    products: {
        all: () => ['products'],
        list: (filters) => ['products', 'list', filters],
        detail: (id) => ['products', 'detail', id],
        categories: () => ['products', 'categories'],
        attributeDefinitions: () => ['products', 'attribute-definitions'],
        attributePrices: (id) => ['products', 'attribute-prices', id],
        deviceSuggestions: () => ['products', 'device-suggestions'],
        devices: (filters) => ['products', 'devices', filters],
        skuResolution: (skus) => ['products', 'sku-resolution', skus],
    },
    imageLibrary: {
        all: () => ['image-library'],
        list: (filters) => ['image-library', 'list', filters],
    },
    libraryDocuments: {
        all: () => ['library-documents'],
        list: (filters) => ['library-documents', 'list', filters],
    },
    units: {
        all: () => ['units'],
        list: (filters) => ['units', 'list', filters],
        counts: (productId) => ['units', 'counts', productId ?? null],
        history: (id) => ['units', 'history', id],
        filterKinds: () => ['units', 'filter-kinds'],
        transitions: (id) => ['units', 'transitions', id],
    },
    stock: {
        all: () => ['stock'],
        levels: (filters) => ['stock', 'levels', filters],
        movements: (filters) => ['stock', 'movements', filters],
        availability: (productId) => ['stock', 'availability', productId],
    },
    customers: {
        all: () => ['customers'],
        list: (filters) => ['customers', 'list', filters],
        detail: (id) => ['customers', 'detail', id],
        devices: (id) => ['customers', 'devices', id],
        phones: (id) => ['customers', 'phones', id],
        paymentMethods: (id) => ['customers', 'payment-methods', id],
        // Own cards plus linked customers' cards (t1117). A separate cache entry
        // from `paymentMethods` — same prefix, so invalidating that refreshes
        // this — because the two lists hold different rows under the same URL.
        chargeableCards: (id) => ['customers', 'payment-methods', id, 'with-linked'],
        links: (id) => ['customers', 'links', id],
        notes: (id) => ['customers', 'notes', id],
        match: (phone) => ['customers', 'match', phone],
        duplicates: (filters = {}) => ['customers', 'duplicates', filters],
        duplicatePairs: (filters = {}) => ['customers', 'duplicates', 'pairs', filters],
        duplicateMatches: (id) => ['customers', 'duplicate-matches', id],
        merges: (filters = {}) => ['customers', 'merges', filters],
        cleanupIds: (filters) => ['customers', 'cleanup-ids', filters],
        sources: () => ['customers', 'sources'],
        cleanupCandidates: (filters) => ['customers', 'cleanup-candidates', filters],
    },
    orders: {
        all: () => ['orders'],
        list: (filters) => ['orders', 'list', filters],
        detail: (id) => ['orders', 'detail', id],
        events: (id) => ['orders', 'events', id],
        transitions: (id) => ['orders', 'transitions', id],
        shipments: (id) => ['orders', 'shipments', id],
    },
    stockRequests: {
        all: () => ['stock-requests'],
        list: (filters) => ['stock-requests', 'list', filters],
        detail: (id) => ['stock-requests', 'detail', id],
        events: (id) => ['stock-requests', 'events', id],
        transitions: (id) => ['stock-requests', 'transitions', id],
        availability: (id) => ['stock-requests', 'availability', id],
    },
    kiosks: {
        all: () => ['kiosks'],
        list: () => ['kiosks', 'list'],
    },
    repairs: {
        all: () => ['repairs'],
        list: (filters) => ['repairs', 'list', filters],
        detail: (id) => ['repairs', 'detail', id],
        events: (id) => ['repairs', 'events', id],
        transitions: (id) => ['repairs', 'transitions', id],
        parts: (id) => ['repairs', 'parts', id],
        labour: (id) => ['repairs', 'labour', id],
    },
    rentals: {
        all: () => ['rentals'],
        list: (filters) => ['rentals', 'list', filters],
        detail: (id) => ['rentals', 'detail', id],
        events: (id) => ['rentals', 'events', id],
        transitions: (id) => ['rentals', 'transitions', id],
        returnScans: (id) => ['rentals', 'return-scans', id],
    },
    timeClock: {
        all: () => ['time-clock'],
        status: () => ['time-clock', 'status'],
        list: (filters) => ['time-clock', 'list', filters],
        summary: (filters) => ['time-clock', 'summary', filters],
        pay: (filters) => ['time-clock', 'pay', filters],
        payRates: () => ['time-clock', 'pay-rates'],
    },
    subscriptions: {
        all: () => ['subscriptions'],
        list: (filters) => ['subscriptions', 'list', filters],
        detail: (id) => ['subscriptions', 'detail', id],
    },
    staff: {
        all: () => ['staff'],
        list: () => ['staff', 'list'],
        active: () => ['staff', 'active'],
        roles: () => ['staff', 'roles'],
    },
    notificationPrefs: () => ['notification-prefs'],
    dashboardPrefs: () => ['dashboard-prefs'],
    staffNotifications: () => ['staff-notifications'],
    important: {
        all: () => ['important'],
        mine: () => ['important', 'mine'],
        recipients: () => ['important', 'recipients'],
        forItem: (itemType, itemId) => ['important', 'item', itemType, itemId],
    },
    navCounts: () => ['nav-counts'],
    supportTicketAlerts: () => ['support-ticket-alerts'],
    supportTickets: {
        all: () => ['support-tickets'],
        list: (filters) => ['support-tickets', 'list', filters],
        detail: (id) => ['support-tickets', 'detail', id],
        messages: (id) => ['support-tickets', 'messages', id],
        earlier: (id) => ['support-tickets', 'earlier', id],
        events: (id) => ['support-tickets', 'events', id],
        transitions: (id) => ['support-tickets', 'transitions', id],
        media: (id) => ['support-tickets', 'media', id],
    },
    tasks: {
        all: () => ['tasks'],
        list: (filters) => ['tasks', 'list', filters],
        detail: (id) => ['tasks', 'detail', id],
        events: (id) => ['tasks', 'events', id],
        replies: (id) => ['tasks', 'replies', id],
    },
    cameras: {
        all: () => ['cameras'],
        media: (filters) => ['cameras', 'media', filters],
        detail: (id) => ['cameras', 'detail', id],
        machineCameras: (machineId) => ['cameras', 'machine', machineId],
    },
    supportSettings: () => ['support-settings'],
    rentalNotificationSettings: () => ['rental-notification-settings'],
    billingCalendarSettings: () => ['billing-calendar-settings'],
    supportCallTargets: () => ['support-call-targets'],
    supportIvrOptions: () => ['support-ivr-options'],
    supportVoiceCallOptions: () => ['support-voice-call-options'],
    supportVoiceTransferTargets: () => ['support-voice-transfer-targets'],
    supportCannedResponses: () => ['support-canned-responses'],
    supportCloseNotes: () => ['support-close-notes'],
    supportReceptionistFaqs: () => ['support-receptionist-faqs'],
    vending: {
        all: () => ['vending'],
        machines: () => ['vending', 'machines'],
        machine: (id) => ['vending', 'machines', id],
        slots: (machineId) => ['vending', 'machines', machineId, 'slots'],
        issues: (filters) => ['vending', 'issues', filters],
        products: () => ['vending', 'products'],
    },
    ai: {
        status: () => ['ai', 'status'],
    },
    mail: {
        all: () => ['mail'],
        mailboxes: () => ['mail', 'mailboxes'],
        mailboxesAll: () => ['mail', 'mailboxes', 'all'],
        conversations: (mailboxId, filters) => ['mail', 'conversations', mailboxId, filters],
        thread: (mailboxId, threadId) => ['mail', 'thread', mailboxId, threadId],
        attachments: (mailboxId, filters) => ['mail', 'attachments', mailboxId, filters],
    },
    purchaseOrders: {
        all: () => ['purchase-orders'],
        list: (filters) => ['purchase-orders', 'list', filters],
        detail: (id) => ['purchase-orders', 'detail', id],
        events: (id) => ['purchase-orders', 'events', id],
    },
    expenses: {
        all: () => ['expenses'],
        payments: (filters) => ['expenses', 'payments', filters],
        summary: (range) => ['expenses', 'summary', range],
        payees: () => ['expenses', 'payees'],
        categories: () => ['expenses', 'categories'],
        recurring: () => ['expenses', 'recurring'],
        balance: (orderId) => ['expenses', 'balance', orderId],
    },
    purchasing: {
        all: () => ['purchasing'],
        costs: (filters) => ['purchasing', 'costs', filters],
        cogs: (filters) => ['purchasing', 'cogs', filters],
        cogsSales: (filters) => ['purchasing', 'cogs-sales', filters],
        salesTax: (month) => ['purchasing', 'sales-tax', month],
    },
    locations: () => ['locations'],
    coupons: {
        all: () => ['coupons'],
        list: (filters) => ['coupons', 'list', filters],
    },
    giftCards: {
        all: () => ['gift-cards'],
        list: (filters) => ['gift-cards', 'list', filters],
        detail: (id) => ['gift-cards', 'detail', id],
        transactions: (id) => ['gift-cards', 'transactions', id],
    },
    storeCredit: (customerId) => ['store-credit', customerId],
    vendorReturns: {
        all: () => ['vendor-returns'],
        list: (filters) => ['vendor-returns', 'list', filters],
        invoice: (vendor, invoiceRef) => ['vendor-returns', 'invoice', vendor, invoiceRef],
    },
    vendors: {
        all: () => ['vendors'],
        list: (filters) => ['vendors', 'list', filters],
        detail: (id) => ['vendors', 'detail', id],
        credit: (id) => ['vendors', 'credit', id],
    },
    search: (q) => ['search', q],
    sessions: () => ['sessions'],
    wiki: {
        all: () => ['wiki'],
        sections: () => ['wiki', 'sections'],
        pages: (filters) => ['wiki', 'pages', filters],
        page: (id) => ['wiki', 'page', id],
        guests: () => ['wiki', 'guests'],
        pageAccess: (id) => ['wiki', 'page', id, 'access'],
        accessCandidates: () => ['wiki', 'access-candidates'],
    },
    workspace: {
        all: () => ['workspace'],
        spaces: () => ['workspace', 'spaces'],
        messages: (spaceId, filters) => ['workspace', 'messages', spaceId, filters],
        thread: (messageId) => ['workspace', 'thread', messageId],
        guests: () => ['workspace', 'guests'],
        dms: () => ['workspace', 'dms'],
        dmCandidates: () => ['workspace', 'dm-candidates'],
    },
};
//# sourceMappingURL=query-keys.js.map